import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { RedoShopifyCountryCode } from "./fulfillment-country-code";

const FulfillmentOrderAddressSchema_ = z.object({
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  company: z.string().nullish(),
  city: z.string().nullish(),
  countryCode: createEnum(RedoShopifyCountryCode, {
    enumName: "RedoShopifyCountryCode",
  }).nullish(),
  locationId: z.string().nullish(),
  name: z.string(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  province: z.string().nullish(),
  zip: z.string().nullish(),
  residential: z.boolean().nullish(),
  verified: z.boolean().nullish(),
  messages: z.array(z.string()).nullish(),
});

export const FulfillmentOrderAddressSchema: z.ZodType<FulfillmentOrderAddress> =
  FulfillmentOrderAddressSchema_;

export interface FulfillmentOrderAddress
  extends z.infer<typeof FulfillmentOrderAddressSchema_> {}

export const FulfillmentOrderAddressWithIdSchema =
  FulfillmentOrderAddressSchema_.extend({ _id: zExt.objectId() });

export type FulfillmentOrderAddressWithId = z.infer<
  typeof FulfillmentOrderAddressWithIdSchema
>;

//https://lorefnon.me/2023/11/28/fixing-inferred-types-exceeding-serializable-length/
export interface IFulfillmentOrderAddress
  extends z.ZodType<FulfillmentOrderAddress> {}

const VerifiedAddressSchema_ = FulfillmentOrderAddressSchema_.extend({
  countryCode: z.string().nullish(),
});
export const VerifiedAddressSchema: z.ZodType<VerifiedAddress> =
  VerifiedAddressSchema_;
export interface VerifiedAddress
  extends z.infer<typeof VerifiedAddressSchema_> {}

// When comparing addresses, only these keys matter.
const equalityKeys: (keyof FulfillmentOrderAddress)[] = [
  "name",
  "email",
  "phone",
  "company",
  "address1",
  "address2",
  "city",
  "province",
  "zip",
  "countryCode",
  "residential",
];
export const addressesEqual = (
  a: FulfillmentOrderAddress | VerifiedAddress,
  b: FulfillmentOrderAddress | VerifiedAddress,
) => equalityKeys.every((k) => (a[k] || null) === (b[k] || null));

// Address fields a merchant edits in the address modal. System-derived flags
// (residential, verified) are intentionally excluded so they cannot trigger
// activity-log entries with an empty diff. Keep in sync with the merchant-app
// label map in shipping-address-diff.ts.
export const userVisibleAddressKeys = [
  "name",
  "company",
  "address1",
  "address2",
  "city",
  "province",
  "zip",
  "countryCode",
  "phone",
  "email",
] as const satisfies readonly (keyof FulfillmentOrderAddress)[];

export type UserVisibleAddressKey = (typeof userVisibleAddressKeys)[number];

export const addressesHaveUserVisibleDifference = (
  a: FulfillmentOrderAddress | VerifiedAddress,
  b: FulfillmentOrderAddress | VerifiedAddress,
) => userVisibleAddressKeys.some((k) => (a[k] || null) !== (b[k] || null));
